import { GridSortDirection, GridSortModel } from '@mui/x-data-grid';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { MAX_ENTITIES_NUMBER_PER_REQUEST, NUMBER_OF_RETRIES } from 'common/constants/exportData';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { getNumberOfRequests, mergeResults } from 'common/utils/exportDataApiHelper';
import { mapUserMembershipDetails } from 'common/utils/userMembershipMapper';

import { APIError } from '..';

import {
    APIRoleUserMembershipDelete,
    APIRoleUserMembershipGetPaginatedList,
    APIRoleUserMembershipGetPaginatedListParams,
    APIRoleUserMembershipPost,
    IAPIRoleUserMembershipGetPaginatedSingleResponse
} from './RoleUserMembership.api';

export const fetchRoleUserPaginatedMemberships = createAsyncThunk('roleUserMemberships/fetchRoleUserMemberships', async (params: APIRoleUserMembershipGetPaginatedListParams) => {
    const response = await APIRoleUserMembershipGetPaginatedList(params);
    return {
        data: _formatRoleUserMembershipResponse(response.data),
        skipped: response.skipped,
        taken: response.taken,
        count: response.count,
    };
});

export const fetchRoleUserPaginatedMembershipsForExport = createAsyncThunk('roleUserMemberships/fetchRoleUserMembershipsForExport', async (params: {
    tenantId: string,
    roleId: string,
    search?: string,
    sortKey?: string,
    sortOrder?: GridSortDirection,
    rowsCount?: number,
    signal: AbortSignal,
    status?:string[]
}) => {
    const numberOfRequests = getNumberOfRequests(params.rowsCount);
    const fetchPromises = Array.from({ length: numberOfRequests }, async (_, i) => APIRoleUserMembershipGetPaginatedList({
        ...params,
        skip: i * MAX_ENTITIES_NUMBER_PER_REQUEST,
        take: MAX_ENTITIES_NUMBER_PER_REQUEST,
        retries: NUMBER_OF_RETRIES
    }));

    const response = (await Promise.all(fetchPromises))
        .map((response)=>{
            return {
                data:  _formatRoleUserMembershipResponse(response.data),
                skipped: response.skipped,
                taken: response.taken
            };
        });

    return mergeResults(response);
});



export const deleteRoleUserMembership = createAsyncThunk('roleUserMembership/deleteRoleUserMembership', async (item: {
    tenantId: string,
    roleId: string,
    userId: string,
    skip?: number,
    take?: number,
    search?: string;
    sortModel?: GridSortModel;
    status?: string[]
}) => {
    try {
        await APIRoleUserMembershipDelete(item);
    }
    catch (error) {
        if (!(error instanceof APIError)) {
            throw error;
        }
        const code = extractStatusCode(error.message);
        if (code !== 404) {
            throw error;
        }
    }

    try {
        const gridData = await APIRoleUserMembershipGetPaginatedList({
            tenantId: item.tenantId,
            roleId: item.roleId,
            skip: item.skip,
            take: item.take,
            search: item.search,
            sortKey: item.sortModel?.[0]?.field,
            sortOrder: item.sortModel?.[0]?.sort,
            status: item.status
        });
        const { count, skipped, taken, data } = gridData;
        const rows = _formatRoleUserMembershipResponse(data);
        return {
            item,
            rows,
            count,
            skipped,
            taken,
        };
    }
    catch(error) {
        if (!(error instanceof APIError)) {
            throw error;
        }
        const code = extractStatusCode(error.message);
        if(code === 404) {
            return {
                item,
                rows: [],
                count: 0,
                skipped: item.skip ?? 0,
                taken: item.take
            };
        }
        throw error;
    }
});

export const postRoleUserMembership = createAsyncThunk('roleUserMembership/createRoleUserMembership', async (item: {
    tenantId: string,
    roleId: string,
    userId: string,
    paginationInfo: {
        skip: number,
        take: number,
        search: string,
        sortModel: GridSortModel | undefined,
    },
    status?: string[]
})=> {
    const response = await APIRoleUserMembershipPost(item);
    const gridData = await APIRoleUserMembershipGetPaginatedList({
        tenantId: item.tenantId,
        roleId: item.roleId,
        skip: item.paginationInfo.skip,
        take: item.paginationInfo.take,
        search: item.paginationInfo.search,
        sortKey: item.paginationInfo.sortModel?.[0]?.field,
        sortOrder: item.paginationInfo.sortModel?.[0]?.sort,
        status: item.status
    });
    const { count, skipped, taken, data } = gridData;
    const rows = _formatRoleUserMembershipResponse(data);
    return {
        item,
        response,
        rows,
        count,
        skipped,
        taken,
    };
});

const _formatRoleUserMembershipResponse = (rows: IAPIRoleUserMembershipGetPaginatedSingleResponse[]) => {
    return rows.map(item =>
    {
        return {
            ...item,
            ...mapUserMembershipDetails(item),
        };
    });
};
